/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import AppTitle from '@/components/common/title';
import { throttle } from '@/utils/helpers/throttle';
import { Reciter } from '@/infrastructure/class/reciter';
import PlayControlButton from '@/components/buttons/play';
import { replaceWords } from '@/utils/helpers/replace-words';
import { useAuth } from '@/infrastructure/context/auth-context';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import ShuffleControlButton from '@/components/buttons/shuffle';
import FavoriteControlButton from '@/components/buttons/favorite';
import { usePlayer } from '@/infrastructure/context/player-context';
import { IAudioSurah } from '@/infrastructure/interfaces/i-audio-surah';
import { useFirebaseDB } from '@/infrastructure/firebase/use-firebase-db';
import { EMPTY_IMAGE_PATH } from '@/infrastructure/const/empty-image-path';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import { getResponsiveImageLoader } from '@/utils/helpers/get-responsive-image-loader';
import s from './styles.module.scss';

interface IReciterDetailHeaderProps {
  data: Reciter;
  audioSurahs: IAudioSurah[];
  allowLike?: boolean;
  serverPage?: number;
  className?: string;
  onReadMoreClick?: () => void;
}

const ReciterDetailHeader: React.FC<IReciterDetailHeaderProps> = (props) => {
  const {
    data,
    audioSurahs,
    serverPage,
    allowLike = true,
    className = '',
    onReadMoreClick = () => {},
  } = props;
  const { player, setTracks, shuffleTracks, setPlaying } = usePlayer();
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [background] = useState<string>(
    !!data.image ? data.image.mediumUrl : EMPTY_IMAGE_PATH
  );

  const { t: tButtons } = useTranslation('buttons');
  const { t: tCommon } = useTranslation('common');

  const router = useRouter();
  const { locale } = router;

  const { authUser, checkAuth, showMessage } = useAuth();
  const { isFavoriteReciter, addToFavoriteReciter, removeFromFavoriteReciter } =
    useFirebaseDB();

  const isFirstPage = serverPage ? serverPage === 1 : true;

  const reciterDetailHeaderClassNames = classNames(s['header'], className);

  const recordedSurah = replaceWords(tCommon('recordedSurah'), {
    count: `${audioSurahs.length}`,
  });

  const title = data.titleEn;

  const audioSurahsItems = useMemo<AudioSurah[]>(() => {
    return sortArrayByFieldName(
      audioSurahs.map((audioSurah) => new AudioSurah(audioSurah, locale)),
      'number'
    );
  }, [audioSurahs, locale]);

  const onFavorite = async () => {
    await checkAuth();
    if (authUser) {
      if (isFavorite) {
        setIsFavorite(false);
        await removeFromFavoriteReciter(authUser.uid, data.guid);
        showMessage(tButtons('removedFromFavorite'));
      } else {
        await addToFavoriteReciter(authUser.uid, data.guid);
        setIsFavorite(true);
        showMessage(tButtons('addedToFavorite'));
      }
    }
  };

  const onPlay = () => {
    const audioSurah = audioSurahsItems[0];
    if (!player.currentTrack || player.currentTrack.slug !== audioSurah.slug) {
      setTracks(audioSurahsItems, audioSurahsItems[0]);
      return;
    }
    if (player.playing) {
      setPlaying(false);
    } else {
      setPlaying(true);
    }
  };

  const onShuffle = useCallback(
    throttle(() => {
      shuffleTracks(audioSurahsItems);
    }, 2000),
    []
  );

  useEffect(() => {
    if (authUser) {
      (async () => {
        const res = await isFavoriteReciter(authUser.uid, data.guid);
        setIsFavorite(res);
      })();
    }
  }, [authUser, data.guid]);

  return (
    <div className={reciterDetailHeaderClassNames}>
      <div className={s['top']}>
        <div className={s['image']}>
          <Image
            className={s['image']}
            alt={data.titleEn}
            src={background}
            loader={
              data.image ? getResponsiveImageLoader(data.image) : undefined
            }
            width={200}
            height={200}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className={s['mob-titles']}>
          <AppTitle title={title} />
          <AppTitle title={data.titleAr} className={s['ar']} />
          <p className={s['mob-surah-count']}>{recordedSurah}</p>
        </div>
      </div>
      <div className={s['info']}>
        <div className={s['desktop-titles']}>
          <AppTitle title={title} main />
          <AppTitle title={data.titleAr} className={s['ar']} />
          <p className={s['surah-count']}>{recordedSurah}</p>
        </div>
        <div className={s['controls']}>
          {allowLike && (
            <FavoriteControlButton active={isFavorite} onClick={onFavorite} />
          )}
          <PlayControlButton active={player.playing} onClick={onPlay} />
          <ShuffleControlButton onClick={onShuffle} />
        </div>
        {isFirstPage && (
          <div className={s['description']}>
            {data.descriptionPreview ? (
              <div>
                <p>{data.descriptionPreview}</p>
                <span className={s['link']} onClick={() => onReadMoreClick()}>
                  {tButtons('readMore')}
                </span>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReciterDetailHeader;
